/**
 * Pure path resolution utilities extracted from server for client use.
 * Enables RouteChangeListener and other client code to resolve page paths
 * without importing from server.
 * Supports both Site.files formats: BasefileDef[] and legacy object.
 */
import { BasefileDef, BasefileFunction, Site, SiteFilesLegacy } from "./models/site.model";

export type ErrorPageKey = "guestNotAllowedPage" | "unauthorizedPage" | "forbiddenPage" | "missingPage" | "defaultFile" | "onlineStartPage";

const ERROR_PAGE_KEY_TO_FUNCTION: Record<ErrorPageKey, BasefileFunction> = {
    guestNotAllowedPage: BasefileFunction.GUEST_NOT_ALLOWED_PAGE,
    unauthorizedPage: BasefileFunction.UNAUTHORIZED_PAGE,
    forbiddenPage: BasefileFunction.FORBIDDEN_PAGE,
    missingPage: BasefileFunction.MISSING_PAGE,
    defaultFile: BasefileFunction.DEFAULT_FILE,
    onlineStartPage: BasefileFunction.ONLINE_START_PAGE,
};

function isLegacyFiles(files: Site["files"]): files is SiteFilesLegacy {
    return files != null && !Array.isArray(files);
}

/**
 * Extracts path from a file configuration (supports BasefileDef[] or legacy object).
 */
export function getErrorPagePath(files: Site["files"], key: ErrorPageKey): string | null {
    if (!files) return null;
    if (isLegacyFiles(files)) {
        return files[key]?.path ?? null;
    }
    const fn = ERROR_PAGE_KEY_TO_FUNCTION[key];
    const def = (files as BasefileDef[]).find((d) => d.function === fn);
    return def?.path ?? null;
}

/**
 * Gets all error page paths from the site files configuration.
 */
export function getPagePaths(files: Site["files"], includeDefault: boolean = true): string[] {
    const errorPageKeys: ErrorPageKey[] = [
        "guestNotAllowedPage",
        "unauthorizedPage",
        "forbiddenPage",
        "missingPage",
        "defaultFile",
        "onlineStartPage",
    ];

    const paths = errorPageKeys
        .map(key => getErrorPagePath(files, key))
        .filter((path): path is string => path !== null);

    if (includeDefault) {
        paths.push("/home.html");
    }
    return paths;
}

/**
 * Gets the first available path from a fallback chain for error pages.
 */
export function getFirstAvailablePagePath(
    files: Site["files"],
    keys: ErrorPageKey[],
    defaultPath: string = "/home.html"
): string {
    for (const key of keys) {
        const path = getErrorPagePath(files, key);
        if (path) {
            return path;
        }
    }
    return defaultPath;
}
